import { Label } from "@radix-ui/react-label";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { GenericTable } from "@shared/components/GenericTable";
import { StepHelpers } from "@shared/components/Stepper";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { Switch } from "@shared/ui/switch";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { AlertCircle, BellRing, CheckCircle2, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";

import {
  generateBills,
  retryGenerateBills,
  trackBillsProgress,
} from "../../api";
import {
  useBulkBillData,
  useDownloadCSV,
  useUpdateConsumerSelection,
} from "../../hooks";

interface ErrorItem {
  consumer_no: string;
  name: string;
  premise: string;
  category: string | null;
  error: string;
  timestamp: string;
}

interface BillGenerationState {
  notifyOnComplete: boolean;
  showProgress: boolean;
  progress: number;
  processed: number;
  successful: number;
  total: number;
  estimatedTime: string;
  isCompleted: boolean;
  run_id: any;
  failedBills: any;
  taskId: any;
  logs: ErrorItem[];
}

interface BillGenerationStepProps {
  selectedMeters?: string[];
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const BillGenerationStep: React.FC<BillGenerationStepProps> = ({
  selectedMeters,
  onNext,
  onPrevious,
  currentStepIndex = 3,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
}) => {
  const { id, action = "add" } = useParams();
  const draftID = stepHelpers.getStepData(0)?.draftId;
  const navigate = useNavigate();
  const [state, setState] = useState<BillGenerationState>({
    notifyOnComplete: true,
    showProgress: false,
    progress: 0,
    processed: 0,
    successful: 0,
    total: 0,
    estimatedTime: "0:13",
    isCompleted: false,
    run_id: null,
    failedBills: null,
    taskId: null,
    logs: [],
  });

  const [failedBillsCurrentPage, setFailedBillsCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [failedBillItems, setFailedBillItems] = useState<any[]>([]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const dataLoadedRef = useRef(false);

  const { data: bulkBillData } = useBulkBillData(id);
  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(stepHelpers.getStepData(0)?.draftId || id);

  // Helper functions for stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Get bill cycle ID from previous step (replaces params?.id)
  const getBillCycleId = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return (
        stepHelpers.getDataFromStep(0, "billCycleId") ||
        stepHelpers.getDataFromStep(0, "workflowBillCycleId") ||
        bulkBillData?.billScheduleData?.id
      );
    } catch (error) {
      console.error("Failed to get bill cycle ID:", error);
      return null;
    }
  }, [stepHelpers]);

  const billCycleId = getBillCycleId();

  // Load saved data from stepHelpers on component mount
  // useEffect(() => {
  //   if (stepHelpers) {
  //     try {
  //       const savedStepData = getStepData();
  //       if (savedStepData) {
  //         // Restore state from saved data
  //         setState((prevState) => ({
  //           ...prevState,
  //           ...savedStepData.billGenerationState,
  //         }));

  //         if (savedStepData.failedBillItems) {
  //           setFailedBillItems(savedStepData.failedBillItems);
  //         }
  //         if (savedStepData.failedBillsCurrentPage) {
  //           setFailedBillsCurrentPage(savedStepData.failedBillsCurrentPage);
  //         }
  //       }
  //     } catch (error) {
  //       console.error("Failed to load step data:", error);
  //     }
  //   }
  // }, [stepHelpers, getStepData]);

  // Save state changes to stepHelpers
  // useEffect(() => {
  //   try {
  //     if (stepHelpers && !dataLoadedRef.current) {
  //       const currentData = getStepData() || {};
  //       const updatedData = {
  //         ...currentData,
  //         billGenerationState: state,
  //         failedBillItems,
  //         failedBillsCurrentPage,
  //         billCycleId,
  //         lastUpdated: new Date().toISOString(),
  //       };
  //       saveStepData(updatedData);
  //       dataLoadedRef.current = true;
  //     }
  //   } catch {
  //     dataLoadedRef.current = true;
  //   }
  // }, [
  //   state,
  //   failedBillItems,
  //   failedBillsCurrentPage,
  //   billCycleId,
  //   saveStepData,
  //   getStepData,
  //   stepHelpers,
  // ]);

  const downloadCsv = useDownloadCSV();

  // Column configuration for failed bills table
  const failedBillsColumns = [
    {
      key: "name",
      header: "Consumer",
    },
    {
      key: "consumerNo",
      header: "Account No",
    },
    {
      key: "errorCode",
      header: "Error Code",
      render: (value: any, row: any) => {
        return row.errorCode;
      },
    },
    {
      key: "error",
      header: "Error Message",
    },
  ];

  const startBillGeneration = (): void => {
    setState((prev) => ({
      ...prev,
      showProgress: true,
      progress: 0,
    }));
    triggerValidation();
  };

  const triggerValidation = async (): Promise<void> => {
    const billID = id || draftID;
    try {
      const response = await generateBills(billCycleId, selectedMeters, billID);
      const taskId = response?.taskId;

      setState((prev) => ({
        ...prev,
        taskId: taskId || null,
      }));

      toast({
        title: "Success",
        description: "Bill generation started successfully",
        variant: "default",
      });

      if (taskId) {
        pollValidationProgress(taskId);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        showProgress: false,
      }));
      toast({
        title: "Error",
        description: "Error starting bill generation",
        variant: "destructive",
      });
    }
  };

  const pollValidationProgress = (taskId: string): void => {
    const interval = setInterval(async () => {
      try {
        const response = await trackBillsProgress(taskId);
        const result = response;

        setState((prev) => ({
          ...prev,
          logs: result?.errors || [],
          progress: result?.percentage || 0,
          processed: result?.processed || 0,
          successful: result?.successful || 0,
          total: result?.total || 0,
          failedBills: result?.failed || 0,
          run_id: result?.runId || null,
        }));
        console.log(result, result?.completed, "responseeeeeeeeeeeeeeeeee");

        if (result?.completed) {
          console.log("complteeeeeeeeeeeeeeeeee");

          clearInterval(interval);
          setState((prev) => ({
            ...prev,
            showProgress: false,
            isCompleted: true,
          }));

          const newErrors = result?.errors;
          const summary = {
            total_consumers: result.total,
            successful: result.successful,
            failed: result.failed,
          };

          // Store in localStorage (equivalent to validationStateService)
          localStorage.setItem("validationErrors", JSON.stringify(newErrors));
          localStorage.setItem("validationSummary", JSON.stringify(summary));
          localStorage.setItem("validationTaskId", taskId);

          // Update failed bill items
          setFailedBillItems(
            (newErrors || []).map((item: any) => ({
              ...item,
              status: "Failed",
            }))
          );

          // Save the run_id for next step via stepHelpers
          if (stepHelpers && result?.runId) {
            stepHelpers.setDataToStep(currentStepIndex, "runId", result.runId);
            // Also store it for easy access by next step
            stepHelpers.setDataToStep(0, "workflowRunId", result.runId);
          }

          // Browser notification if enabled and supported
          if (state.notifyOnComplete && "Notification" in window) {
            if (Notification.permission === "granted") {
              new Notification("Bill Generation Complete", {
                body: `Successfully processed ${result.successful} of ${result.total} bills.`,
              });
            } else if (Notification.permission !== "denied") {
              Notification.requestPermission();
            }
          }
        }
      } catch (error) {
        clearInterval(interval);
        setState((prev) => ({
          ...prev,
          showProgress: false,
        }));
        toast({
          title: "Error",
          description: "Error while tracking bill generation",
          variant: "destructive",
        });
      }
    }, 15000); // Poll every 5 seconds

    intervalRef.current = interval;
  };

  const retryBillGeneration = async (): Promise<void> => {
    setState((prev) => ({
      ...prev,
      showProgress: true,
      isCompleted: false,
      progress: 0,
    }));

    try {
      const payload = {
        schedule_id: billCycleId,
        remote_utility_id: getRemoteUtilityId(),
      };
      const response = await retryGenerateBills(payload, state.run_id);
      const taskId = response?.taskId;

      setState((prev) => ({
        ...prev,
        taskId: taskId || null,
      }));

      toast({
        title: "Success",
        description: "Bill generation started successfully",
        variant: "default",
      });
      if (taskId) {
        pollValidationProgress(taskId);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        showProgress: false,
      }));
      toast({
        title: "Error",
        description: "Error starting bill generation",
        variant: "destructive",
      });
    }
  };

  const retrySelected = (): void => {
    toast({
      title: "Info",
      description: "Retrying selected bills...",
      variant: "default",
    });
  };

  const onClickDownload = async () => {
    try {
      await downloadCsv.mutateAsync(state.taskId);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // const exportLog = (): void => {
  //   toast.info("Exporting log...");
  // };

  const handleNext = async () => {
    // Save final step data with completion status
    const finalStepData = {
      billGenerationState: state,
      failedBillItems,
      failedBillsCurrentPage,
      billCycleId,
      runId: state.run_id, // This is the ID that next step needs
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    };

    const payload = {
      step: {
        step_working_on: 4,
        total_steps: 5,
      },
      bill_data: {
        bill_run_id: state.run_id,
      },
    };

    const response = await updateConsumer(payload);

    if (response) {
      saveStepData(finalStepData);
      if (onNext) {
        onNext();
      }
    }
  };

  const handleDraft = async () => {
    const payload = {
      step: {
        step_working_on: 3,
        total_steps: 5,
      },
    };
    const response = await updateConsumer(payload);

    const finalStepData = {
      billGenerationState: state,
      failedBillItems,
      failedBillsCurrentPage,
      billCycleId,
      runId: state.run_id, // This is the ID that next step needs
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    };

    if (response) {
      saveStepData(finalStepData);
      navigate("/bx/history?tab=bulk&tab1=draft");
      await queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.billHistory.BulkBillDraft(), 1],
        exact: false,
      });
    }
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className="px-3 py-4 rounded ">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 col-span-12 md:col-span-8">
          <h2 className="text-xl font-semibold">Bill Generation Process</h2>
          <p className="text-sm text-gray-500 mb-4">
            Start the bill generation process and monitor progress
          </p>

          {!state.showProgress && !state.isCompleted && (
            <div className="mb-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="notify-complete"
                  checked={state.notifyOnComplete}
                  onCheckedChange={(checked) =>
                    setState((prev) => ({ ...prev, notifyOnComplete: checked }))
                  }
                />
                <Label
                  htmlFor="notify-complete"
                  className="card-subheading-text flex items-center"
                >
                  <BellRing className="w-4 h-4 mr-2" />
                  Notify me when bill generation is complete
                </Label>
              </div>

              <Alert className="mt-5 mb-5">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Bill Generation Information</AlertTitle>
                <AlertDescription className="text-xs">
                  You are about to generate bills for selected cycle. process
                  may take several minutes for large billing cycles.
                </AlertDescription>
              </Alert>

              {/* Button */}
              <Button onClick={startBillGeneration}>
                Start Bill Generation
              </Button>
            </div>
          )}

          {/* Progress UI */}
          {(state.showProgress || state.isCompleted) && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <div>
                  <p className="text-md font-semibold">Progress</p>
                  {state.progress < 100 && (
                    <p className="text-sm text-gray-500">Generating Bills...</p>
                  )}
                </div>

                <Button variant="outline" onClick={retryBillGeneration}>
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Retry Failed
                </Button>
              </div>

              {/* Show Progress Bar */}
              <Progress value={state.progress} className="h-2" />

              <div className="flex justify-between text-sm mt-1 text-gray-600">
                {state.progress < 100 ? (
                  <span>{state.progress}%</span>
                ) : (
                  <div className="flex items-center text-sm text-success">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Generation
                    completed successfully
                  </div>
                )}
                <span>
                  Processed: ~{state.processed} of {state.total}
                </span>
              </div>

              {/* Info box */}
              {state.progress < 100 && (
                <div className="bg-blue-50 border border-blue-100 rounded p-4 mt-4 text-sm text-blue-700">
                  <p className="font-medium">
                    Bill generation is in progress. Please do not navigate away
                    from this page.
                  </p>
                  <p>
                    Partial batches will be committed if the process is
                    interrupted.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <Card className="col-span-12 md:col-span-4">
          <CardHeader>
            <CardTitle>Generation Summary</CardTitle>
            <CardDescription>
              Statistics for the current generation process
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!state.isCompleted ? (
              <div className="text-center text-gray-400 mt-6">
                No generation data available
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Total Bills</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {state.total}
                  </div>
                </div>

                <div className="flex justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600 mb-1">Generated</div>
                    <div className="text-xl font-bold text-green-600">
                      {state.successful}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-600 mb-1">Failed</div>
                    <div className="text-xl font-bold text-red-600">
                      {state.failedBills}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Generation Log */}
      <Card className="mt-4 mb-4">
        <CardHeader>
          <CardTitle>Generation Log</CardTitle>
          <CardDescription>Process logs and error messages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded p-4 h-60 overflow-auto font-mono text-sm text-gray-700 whitespace-pre-line">
            {state.logs.length > 0 ? (
              state.logs.map((log, index) => (
                <div key={index}>
                  <span>
                    {log.timestamp} {log.error}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400">
                No log entries yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Failed Bills Section */}
      {failedBillItems?.length > 0 && (
        <Card className="mt-4 mb-4">
          <CardHeader>
            <CardTitle>Failed Bills</CardTitle>
            <CardDescription>
              Bills that could not be generated due to errors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <GenericTable
              data={failedBillItems ?? []}
              columns={failedBillsColumns}
              rowKey="id"
              actionsType="buttons"
              emptyMessage="No failed bills found"
              loading={false}
              pagination={{
                currentPage: failedBillsCurrentPage,
                totalPages: Math.ceil(
                  (failedBillItems?.length || 0) / itemsPerPage
                ),
                itemsPerPage: itemsPerPage,
                totalItems: failedBillItems?.length || 0,
                startItem: (failedBillsCurrentPage - 1) * itemsPerPage + 1,
                endItem: Math.min(
                  failedBillsCurrentPage * itemsPerPage,
                  failedBillItems?.length || 0
                ),
              }}
              onPageChange={setFailedBillsCurrentPage}
            />
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between mt-6">
        {/* {state.isCompleted && (
          <Button variant="outline" onClick={exportLog}>
            <FileDown className="mr-2 h-4 w-4" />
            Export Log
          </Button>
        )} */}
        <Button variant="outline" onClick={onPrevious} disabled={isFirstStep}>
          Previous
        </Button>
        <div className="flex gap-4">
          <Button onClick={handleDraft}>Save As Draft</Button>
          <Button onClick={handleNext} disabled={!state.isCompleted}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BillGenerationStep;
